import React, { useState, useEffect } from "react";
import { X, FolderOpen, Plus, Trash2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import s from "../../pages/ProductionSuite.module.css";
import { appPrompt, appConfirm } from "@/components/AppDialogHost";

const LS_KEY = "ps_projects";

export function PSProjectsModal({ currentProject, onLoad, onClose }) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    try { setProjects(JSON.parse(localStorage.getItem(LS_KEY) || "[]")); }
    catch { setProjects([]); }
  }, []);

  const newProject = async () => {
    const name = await appPrompt("Name your new project", "Untitled session");
    if (!name) return;
    onLoad({ name, fresh: true });
    onClose();
  };

  const removeProject = async (name) => {
    if (!(await appConfirm(`Delete project "${name}"?`))) return;
    const next = projects.filter((p) => p.name !== name);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setProjects(next);
    toast.success("Project deleted");
  };

  return (
    <div className={s.modalBackdrop} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()} data-testid="ps-projects-modal">
        <div className={s.modalHeader}>
          <span><FolderOpen size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} /> Projects</span>
          <button type="button" onClick={onClose} data-testid="ps-projects-close" className={s.btnSmall}><X size={11} /></button>
        </div>
        <div className={s.modalBody}>
          <button
            type="button"
            onClick={newProject}
            className={`${s.btnSmall} ${s.btnPrimary}`}
            style={{ marginBottom: 12 }}
            data-testid="ps-projects-new"
          >
            <Plus size={11} /> New project
          </button>
          {projects.length === 0 ? (
            <div style={{ fontSize: 12, color: "#4a5068", textAlign: "center", padding: 24 }}>
              No saved projects yet. Hit &ldquo;Save&rdquo; (or wait 2 minutes for auto-save) to land your first session.
            </div>
          ) : (
            projects.map((p) => (
              <div
                key={p.name}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", marginBottom: 6,
                  border: "0.5px solid #1e2130", borderRadius: 6,
                  background: p.name === currentProject ? "#0d1e35" : "#0a0d14",
                }}
                data-testid={`ps-project-${p.name.replace(/\s+/g, "-")}`}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "#c8ccd8" }}>{p.name}</div>
                  <div style={{ fontSize: 10, color: "#4a5068" }}>
                    {(p.clips || []).length} clip{(p.clips || []).length === 1 ? "" : "s"} ·
                    Saved {new Date(p.savedAt || Date.now()).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { onLoad(p); onClose(); }}
                  className={`${s.btnSmall} ${s.btnPrimary}`}
                  data-testid={`ps-project-open-${p.name.replace(/\s+/g, "-")}`}
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => removeProject(p.name)}
                  className={s.btnSmall}
                  title="Delete"
                  data-testid={`ps-project-delete-${p.name.replace(/\s+/g, "-")}`}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


export function PSExportModal({ projectName, tracks = [], clips = [], volumes = {}, muted = {}, solo = {}, onClose }) {
  const [format, setFormat] = useState("mp3-320");
  const [fileName, setFileName] = useState(projectName);
  const [includePad, setIncludePad] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // True when at least one clip has a resolvable source asset (drag-imported
  // or production-imported). Demo-only sessions report 0 here and we surface
  // a clearer CTA copy instead of letting the user hit a 400.
  const realClips = (clips || []).filter((c) => c.assetId || c.asset_id);
  const canMix = realClips.length > 0;

  const handleExport = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      // Build the session payload the backend mixes down.
      const payloadClips = clips
        .filter((c) => c.assetId || c.asset_id)  // need asset_id to resolve audio
        .map((c) => ({
          track: c.track,
          name: c.name || "",
          start: Number(c.start) || 0,
          duration: Number(c.duration) || 0,
          asset_id: c.assetId || c.asset_id || "",
          audio_url: c.audioUrl || c.audio_url || "",
        }));
      const mutedList = Object.entries(muted || {}).filter(([, v]) => v).map(([k]) => k);
      const soloList = Object.entries(solo || {}).filter(([, v]) => v).map(([k]) => k);
      const r = await api.post("/production/export", {
        project_name: projectName,
        format,
        file_name: fileName,
        tracks: tracks.map((t) => ({ id: t.id, name: t.name || "" })),
        clips: payloadClips,
        volumes: volumes || {},
        muted: mutedList,
        solo: soloList,
      });
      const data = r.data || {};
      setResult(data);
      if (data.stub) {
        toast.message(data.message || "Mixdown queued");
      } else {
        toast.success(`Mixdown ready · ${data.clips_mixed} clip${data.clips_mixed === 1 ? "" : "s"} bounced`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setSubmitting(false); }
  };

  // Build absolute download URL (audio_url is relative)
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const downloadHref = result?.audio_url ? `${apiBase}${result.audio_url}` : null;

  return (
    <div className={s.modalBackdrop} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()} data-testid="ps-export-modal">
        <div className={s.modalHeader}>
          <span><Download size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} /> Export mix</span>
          <button type="button" onClick={onClose} data-testid="ps-export-close" className={s.btnSmall}><X size={11} /></button>
        </div>
        <div className={s.modalBody}>
          <div className={s.modalRow}>
            <div className={s.modalLabel}>Format</div>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className={s.modalInput}
              data-testid="ps-export-format"
            >
              <option value="mp3-128">MP3 · 128 kbps</option>
              <option value="mp3-320">MP3 · 320 kbps</option>
              <option value="wav-16">WAV · 16-bit</option>
              <option value="wav-24">WAV · 24-bit</option>
              <option value="flac">FLAC</option>
            </select>
          </div>
          <div className={s.modalRow}>
            <div className={s.modalLabel}>File name</div>
            <input
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className={s.modalInput}
              data-testid="ps-export-filename"
            />
          </div>
          <div className={s.modalRow}>
            <label style={{ fontSize: 11, color: "#c8ccd8", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={includePad}
                onChange={(e) => setIncludePad(e.target.checked)}
                data-testid="ps-export-pad"
              />
              Include silence padding (0.5s head + tail)
            </label>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={submitting || !canMix}
            className={`${s.btnSmall} ${s.btnPrimary}`}
            style={{ width: "100%", padding: "10px 14px", fontSize: 12, justifyContent: "center" }}
            data-testid="ps-export-submit"
          >
            {submitting ? "Rendering…" : !canMix ? "Add clips first" : "Render mixdown"}
          </button>
          {result && !result.stub && downloadHref && (
            <div style={{
              marginTop: 12, padding: "10px 12px", borderRadius: 6,
              background: "rgba(74,158,255,0.08)", border: "1px solid rgba(74,158,255,0.3)",
            }}>
              <div style={{ fontSize: 11, color: "#a9b3c9", marginBottom: 6 }}>
                {result.clips_mixed} clip{result.clips_mixed === 1 ? "" : "s"} bounced · {Math.round((result.size_bytes || 0) / 1024)} KB
              </div>
              <a
                href={downloadHref}
                download={result.file_name}
                data-testid="ps-export-download"
                className={`${s.btnSmall} ${s.btnPrimary}`}
                style={{ display: "inline-flex", padding: "8px 14px", fontSize: 12, textDecoration: "none" }}
              >
                <Download size={11} style={{ marginRight: 6 }} /> Download {result.file_name}
              </a>
            </div>
          )}
          {result && result.stub && (
            <div style={{ fontSize: 10, color: "#4a5068", marginTop: 8, textAlign: "center" }}>
              {result.message}
            </div>
          )}
          {!result && (
            <div style={{ fontSize: 10, color: "#4a5068", marginTop: 8, textAlign: "center" }}>
              ffmpeg mixdown runs server-side · normalizes + bounces in 5–15s
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
